import { useEffect, useRef, useState, useCallback } from "react";
import { evaluatePickAgainstScore } from "@/lib/pickStatus";

const LIVE_CODES = new Set([
    "1H",
    "HT",
    "2H",
    "ET",
    "BT",
    "P",
    "SUSP",
    "INT",
    "LIVE",
]);
const FINISHED_CODES = new Set(["FT", "AET", "PEN"]);
const STORAGE_KEY = "kickloom.pickAlerts.optedOut";

const phaseOf = (live) => {
    if (!live) return "upcoming";
    const short = (live.status?.short || "").toUpperCase();
    if (FINISHED_CODES.has(short)) return "finished";
    if (LIVE_CODES.has(short)) return "live";
    return "upcoming";
};

// Resolve the current "state" of a pick into one of a small set of labels
// so we can detect meaningful transitions without spamming on every tick.
const resolveState = (pick, live) => {
    const phase = phaseOf(live);
    if (phase === "upcoming") return "upcoming";
    const bet = evaluatePickAgainstScore(pick, live.goals?.home, live.goals?.away);
    if (phase === "finished") {
        if (bet === "on_track") return "won";
        if (bet === "slipping") return "lost";
        if (bet === "tight") return "void";
        return "settled"; // couldn't decode — treat as terminal but quiet
    }
    // live
    if (bet === "on_track") return "live_on_track";
    if (bet === "slipping") return "live_slipping";
    return "live_unknown";
};

// Which transitions are worth bothering the user about, and the message
// template for each. Source state -> target state -> { title, suffix }.
const TRANSITION_MESSAGES = {
    upcoming: {
        live_on_track: { title: "🟢 ON TRACK", body: "kicked off — bet is winning" },
        live_slipping: { title: "🔴 SLIPPING", body: "kicked off — bet is losing" },
        won: { title: "✅ WON", body: "settled" },
        lost: { title: "❌ LOST", body: "settled" },
        void: { title: "⚪ VOID", body: "refunded" },
    },
    live_on_track: {
        live_slipping: { title: "🔴 SLIPPING", body: "bet just turned losing" },
        won: { title: "✅ WON", body: "final whistle" },
        lost: { title: "❌ LOST", body: "final whistle" },
        void: { title: "⚪ VOID", body: "refunded" },
    },
    live_slipping: {
        live_on_track: { title: "🟢 BACK ON TRACK", body: "bet just turned winning" },
        won: { title: "✅ WON", body: "final whistle" },
        lost: { title: "❌ LOST", body: "final whistle" },
        void: { title: "⚪ VOID", body: "refunded" },
    },
    live_unknown: {
        won: { title: "✅ WON", body: "final whistle" },
        lost: { title: "❌ LOST", body: "final whistle" },
    },
};

const supportsNotifications = () =>
    typeof window !== "undefined" && "Notification" in window;

const fireNotification = (title, body) => {
    if (!supportsNotifications()) return;
    if (Notification.permission !== "granted") return;
    try {
        new Notification(title, {
            body,
            icon: "/favicon.ico",
            tag: `kickloom-${title}-${body}`, // collapse duplicates
        });
    } catch {
        // Some browsers (notably iOS Safari) throw on direct construction
        // outside a Service Worker — silently ignore, the in-app toast
        // already tells the user.
    }
};

/**
 * Watch the picks accessible to this user and fire a browser notification
 * whenever a pick transitions between meaningful states (kickoff, on-track
 * <-> slipping flip, final whistle).
 *
 * Returns `{ permission, requestPermission, enabled }` so the UI can wire
 * an "Enable alerts" button.
 */
export default function usePickTransitions(tiers, liveByFixture, userLevel) {
    const lastStateRef = useRef(null); // Map<fixtureId, state>
    const [permission, setPermission] = useState(() =>
        supportsNotifications() ? Notification.permission : "unsupported"
    );
    const [optedOut, setOptedOut] = useState(() => {
        if (typeof window === "undefined") return false;
        return localStorage.getItem(STORAGE_KEY) === "1";
    });

    const requestPermission = useCallback(async () => {
        if (!supportsNotifications()) return "unsupported";
        try {
            const result = await Notification.requestPermission();
            setPermission(result);
            if (result === "denied") {
                localStorage.setItem(STORAGE_KEY, "1");
                setOptedOut(true);
            } else {
                localStorage.removeItem(STORAGE_KEY);
                setOptedOut(false);
            }
            return result;
        } catch {
            return "denied";
        }
    }, []);

    useEffect(() => {
        const accessibleTiers =
            userLevel === "vip" ? ["2", "5", "10"] : ["2"];
        const picks = accessibleTiers
            .flatMap((t) => tiers?.[t] || [])
            .filter((p) => p && p.fixtureId);
        if (!picks.length) return;

        // Build the current state snapshot.
        const next = new Map();
        for (const p of picks) {
            const live = liveByFixture?.[String(p.fixtureId)];
            next.set(p.fixtureId, resolveState(p, live));
        }

        const prev = lastStateRef.current;
        lastStateRef.current = next;

        // First pass: bootstrap the snapshot, never fire on initial render.
        if (prev == null) return;
        if (permission !== "granted") return;

        for (const p of picks) {
            const before = prev.get(p.fixtureId);
            const after = next.get(p.fixtureId);
            if (!before || before === after) continue;
            const messages = TRANSITION_MESSAGES[before];
            const msg = messages && messages[after];
            if (!msg) continue;
            const matchLabel = p.match || p.matchName || "Match";
            fireNotification(
                `${msg.title} · ${matchLabel}`,
                `${p.prediction || "Your pick"} — ${msg.body}`
            );
        }
    }, [tiers, liveByFixture, userLevel, permission]);

    return {
        permission,
        optedOut,
        requestPermission,
        supported: supportsNotifications(),
        enabled: permission === "granted",
    };
}
